import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { useBrowserScreen } from './useBrowserScreen';

type TBurgerMenu = {
  isOpen: boolean;
  burgerBtnRef: RefObject<HTMLButtonElement>;
  handleBurgerClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleMobileNavigationItemClick: () => void;
  handleMobileLogoClick: () => void;
  burgerOffsetTop: number;
};

export const useBurgerMenu = (
  headerReference: RefObject<HTMLElement>
): TBurgerMenu => {
  const DOCUMENT_LOCK_CLASSNAME = 'cds-scroll-locked';

  const { isMobileView } = useBrowserScreen();
  const [isOpen, setIsOpen] = useState(false);
  const [burgerOffsetTop, setBurgerOffsetTop] = useState(0);
  const burgerBtnRef = useRef<HTMLButtonElement | null>(null);

  const handleBurgerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const headerEl = headerReference.current;
    if (headerEl) {
      setBurgerOffsetTop(
        headerEl.getBoundingClientRect().top + headerEl.offsetHeight
      );
    }

    setIsOpen(!isOpen);
  };

  const handleMobileNavigationItemClick = () => {
    if (isMobileView) setIsOpen(false);
  };

  const handleMobileLogoClick = () => {
    setIsOpen(false);
  };

  const handler = useCallback(() => {
    if (!isMobileView) {
      setIsOpen(false);
    }
  }, [isMobileView]);

  useEffect(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.documentElement.classList.add(DOCUMENT_LOCK_CLASSNAME);
    } else {
      document.documentElement.classList.remove(DOCUMENT_LOCK_CLASSNAME);
    }

    if (burgerBtnRef.current) {
      burgerBtnRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);

    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
    };
  }, [handler]);

  return {
    isOpen,
    burgerBtnRef,
    handleBurgerClick,
    burgerOffsetTop,
    handleMobileNavigationItemClick,
    handleMobileLogoClick,
  };
};
